import React, { useMemo } from 'react';
import {
  DatePicker,
  InputNumber,
  Input,
  Radio,
  Form,
  Typography,
  Tooltip,
  Select,
  Row,
  Col,
  Button,
} from 'antd';

import classnames from 'classnames';

import { WrapperSelect } from 'common/components';
import {
  QuestionCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  CloseCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { makeRules } from 'pages/branded-products/components/post-to-walmart-krogger/post-product-form/utils';
import { DATE_FORMAT } from 'static/Constants';
import {
  SelectProductImageUrlInput,
  SelectProductImageUrlInputMultiple,
} from 'pages/branded-products/components/post-to-walmart-krogger/post-product-form/select-product-image-url-input';
import { ServerErrorIndicator } from 'pages/branded-products/components/post-to-walmart-krogger/post-product-form/ServerErrorIndicator';
import { useProductDetail } from '../ProductDetailContext';
import { useGetWalmartFormItemError } from 'pages/branded-products/components/post-to-walmart-krogger/hooks';
import {
  GroupElementWrapper,
  HeaderGroupElement,
} from 'pages/product-full-view/shared/components';
import { WalmartFields } from './WalmartFields';
import { getPrecisionFromStep } from './utils';

const { Text } = Typography;

export const PostField = (props) => {
  const {
    label,
    min,
    max,
    minLength,
    maxLength,
    type,
    fullKey,
    enums,
    description,
    isRequiredProperty,
    serverError,
    showServerError,
    name,
    minItems,
    ruleType,
    productId,
    filterOption,
    isRivirMappingField,
    rivirMappingField,
    step,
  } = props;

  const {
    advancedModule: { foundFieldFullPath: advancedFoundFieldFullPath },
  } = useProductDetail();

  const fieldName =
    name !== null && name !== undefined ? name : fullKey?.split('.');

  const fieldFullPath = 'walmartProductInformation.' + fullKey;

  return (
    <Form.Item
      name={fieldName}
      label={
        label && (
          <>
            <Tooltip title={label}>
              <Text
                ellipsis
                className={classnames({
                  'product-detail-view__label-form-item': true,
                  [`product-detail-view__label-form-item--${fieldFullPath}`]: true,
                  'product-detail-view__label-form-item--found-field':
                    fieldFullPath?.toLowerCase() ===
                    advancedFoundFieldFullPath?.toLowerCase(),
                })}
                strong
              >
                {label}
              </Text>
            </Tooltip>
            {isRivirMappingField && (
              <Tooltip
                title={
                  rivirMappingField
                    ? `Auto populate from RIVIR: ${rivirMappingField}`
                    : ''
                }
                color='#238dd9'
              >
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            )}
            {description && (
              <Tooltip title={description} color='#238dd9'>
                <QuestionCircleOutlined className='product-detail-view__hint-button' />
              </Tooltip>
            )}
          </>
        )
      }
      rules={makeRules({ isRequiredProperty, ruleType, minItems })}
      help={serverError && <Text type='danger'> {serverError?.error} </Text>}
    >
      {showServerError ? (
        <PostFieldInputWrapper serverError={serverError}>
          <PostFieldInput
            label={label}
            type={type}
            enums={enums}
            min={min}
            max={max}
            minLength={minLength}
            maxLength={maxLength}
            productId={productId}
            fullKey={fullKey}
            filterOption={filterOption}
            step={step}
          />
        </PostFieldInputWrapper>
      ) : (
        <PostFieldInput
          label={label}
          type={type}
          enums={enums}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          productId={productId}
          fullKey={fullKey}
          filterOption={filterOption}
          step={step}
        />
      )}
    </Form.Item>
  );
};

const PostFieldInput = (props) => {
  const {
    type,
    enums,
    fullKey,
    productId,
    filterOption,
    step,
    label,
    ...restProps
  } = props;

  if (type === 'dateTime') {
    return (
      <DatePicker
        format={DATE_FORMAT.SYSTEM_FORMAT}
        style={{ minWidth: 200 }}
        {...restProps}
      />
    );
  }

  if (type === 'number') {
    const precision = getPrecisionFromStep(step);

    return (
      <InputNumber
        placeholder={`Please Input ${label}`}
        style={{ minWidth: 200 }}
        precision={precision}
        step={step}
        {...restProps}
      />
    );
  }

  if (type === 'integer') {
    const precision = getPrecisionFromStep(step);
    return (
      <InputNumber
        placeholder={`Please Input ${label}`}
        parser={(value) => value.replace(/\.\d*/, '')}
        style={{ minWidth: 200 }}
        precision={precision}
        step={step}
        {...restProps}
      />
    );
  }

  if (type === 'yes/no') {
    return (
      <Radio.Group {...restProps}>
        <Radio value={'yes'}>Yes</Radio>
        <Radio value={'no'}>No</Radio>
      </Radio.Group>
    );
  }

  if (type === 'true/false') {
    return (
      <Radio.Group {...restProps}>
        <Radio value={true}>True</Radio>
        <Radio value={false}>False</Radio>
      </Radio.Group>
    );
  }

  if (type === 'select') {
    if (enums) {
      return (
        <WrapperSelect
          showSearch
          placeholder={`Please Select ${label}`}
          filterOption={filterOption}
          {...restProps}
        >
          {enums
            ?.slice()
            .sort()
            .map((enumValue, index) => (
              <Select.Option value={enumValue} key={index}>
                {enumValue}
              </Select.Option>
            ))}
        </WrapperSelect>
      );
    }
  }

  if (type === 'multi-select') {
    if (!!enums) {
      return (
        <WrapperSelect
          showSearch
          placeholder={`Please Select ${label}`}
          mode='multiple'
          filterOption={filterOption}
          {...restProps}
        >
          {enums
            ?.slice()
            .sort()
            .map((enumValue, index) => (
              <Select.Option value={enumValue} key={index}>
                {enumValue}
              </Select.Option>
            ))}
        </WrapperSelect>
      );
    }
  }

  if (type === 'product-image-url') {
    return <SelectProductImageUrlInput {...restProps} productId={productId} />;
  }

  if (type === 'product-image-url-multiple') {
    return (
      <SelectProductImageUrlInputMultiple
        {...restProps}
        productId={productId}
      />
    );
  }

  return (
    <Input
      placeholder={`Please Input ${label}`}
      autoFocus
      style={{ width: '100%' }}
      {...restProps}
    />
  );
};

const PostFieldInputWrapper = ({ children, serverError, ...restProps }) => {
  const renderChildren = () => {
    return React.Children.map(children, (child) => {
      return React.cloneElement(child, restProps);
    });
  };

  return (
    <Row align='middle'>
      <Col style={{ flex: 1, overflow: 'hidden' }}>{renderChildren()}</Col>
      <Col>
        <ServerErrorIndicator serverError={serverError} />
      </Col>
    </Row>
  );
};

const PostFieldList = (props) => {
  const {
    label,
    items,
    minItems,
    fullKey,
    isRequiredProperty,
    productId,
    serverError,
    description,
    isRivirMappingField,
    rivirMappingField,
  } = props;

  const { childProperties: childPropertiesOfItem } = items;

  const errorShowType = serverError ? 'showServerError' : 'showAntdError';

  return (
    <div style={{ marginBottom: 8 }}>
      <Form.List
        name={fullKey?.split('.')}
        rules={makeRules({
          isRequiredProperty,
          ruleType: 'formList',
          minItems,
        })}
      >
        {(fields, { add, remove }, formListParams) => {
          const errors = formListParams?.errors;

          return (
            <div>
              {!childPropertiesOfItem && (
                <Row>
                  <Col xs={24}>
                    <AddListItemButton
                      label={label}
                      listType='singleFieldList'
                      add={add}
                      fullKey={fullKey}
                      description={description}
                      isRequiredProperty={isRequiredProperty}
                      isRivirMappingField={isRivirMappingField}
                      rivirMappingField={rivirMappingField}
                    />
                  </Col>
                </Row>
              )}

              {childPropertiesOfItem ? (
                <Row>
                  <Col xs={24}>
                    <MultipleFieldList
                      fields={fields}
                      label={label}
                      items={items}
                      fullKey={fullKey}
                      remove={remove}
                      add={add}
                      productId={productId}
                    />
                  </Col>
                </Row>
              ) : (
                <div>
                  <Row>
                    <Col
                      style={{ flex: 1, overflow: 'hidden', marginLeft: 300 }}
                    >
                      <SingleFieldList
                        fields={fields}
                        minItems={minItems}
                        items={items}
                        fullKey={fullKey}
                        remove={remove}
                        productId={productId}
                      />
                    </Col>
                    <Col flex='30px' />
                  </Row>
                  {errorShowType === 'showServerError' && (
                    <Row>
                      <Col xs={12} style={{ marginLeft: 300 }}>
                        {serverError && (
                          <Text type='danger'>{serverError?.error}</Text>
                        )}
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {!!childPropertiesOfItem && (
                <>
                  <Row>
                    <Col xs={24}>
                      <AddListItemButton
                        label={label}
                        listType='multipleFieldList'
                        add={add}
                        fullKey={fullKey}
                        isRequiredProperty={isRequiredProperty}
                        isRivirMappingField={isRivirMappingField}
                        rivirMappingField={rivirMappingField}
                      />
                    </Col>
                  </Row>
                  {errorShowType === 'showServerError' && (
                    <Row>
                      <Col xs={12} style={{ marginLeft: 300 }}>
                        {serverError && (
                          <Text type='danger'>{serverError?.error}</Text>
                        )}
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {errorShowType === 'showAntdError' && errors.length > 0 ? (
                <Row style={{ width: '100%' }}>
                  <Col span={12} style={{ marginLeft: 300 }}>
                    <Text type='danger'>{errors[0]}</Text>
                  </Col>
                </Row>
              ) : null}
            </div>
          );
        }}
      </Form.List>
    </div>
  );
};

//*List of group fields
const MultipleFieldList = ({
  fields,
  label,
  items,
  remove,
  productId,
  fullKey,
  add,
}) => {
  const { childProperties: childPropertiesOfItem } = items;

  return (
    <>
      {fields?.map((fieldItem, idx) => {
        if (!childPropertiesOfItem) return null;
        const errorKey = `${fullKey}[${fieldItem?.name}]`;
        return (
          <FieldGroup
            key={fieldItem.key}
            childProperties={childPropertiesOfItem}
            fieldName={label}
            fieldItem={fieldItem}
            index={idx}
            add={add}
            remove={remove}
            fullKey={fullKey}
            productId={productId}
            errorKey={errorKey}
          />
        );
      })}
    </>
  );
};

//* group field
const FieldGroup = ({
  childProperties,
  fieldName,
  index,
  fieldItem,
  remove,
  productId,
  errorKey,
  add,
  fullKey,
}) => {
  const title = fieldName ? `${fieldName} [${index + 1}]` : null;

  const form = Form.useFormInstance();

  const handleCopyField = () => {
    const currentFieldValue = form.getFieldValue([
      ...fullKey.split('.'),
      `${index}`,
    ]);

    const copiedValue =
      currentFieldValue && JSON.parse(JSON.stringify(currentFieldValue));

    add(copiedValue);
  };

  return (
    <Row>
      <Col flex={1} style={{ paddingRight: 40 }}>
        <GroupElementWrapper
          flex='auto'
          style={{ margin: 24, marginBottom: 6 }}
          className='product-detail__group-element'
        >
          <HeaderGroupElement header={title} />
          {childProperties?.map((childPropertyItem) => {
            return (
              <WalmartFields
                {...childPropertyItem}
                name={[fieldItem?.name, childPropertyItem?.key]}
                productId={productId}
                errorKey={`${errorKey}.${childPropertyItem?.key}`}
              />
            );
          })}
        </GroupElementWrapper>
      </Col>
      <Col>
        <div
          style={{
            top: 25,
            right: 12,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <RemoveListItemButton remove={remove} field={fieldItem} />
          <Button
            type='primary'
            className='product-detail__btn-copy'
            icon={<CopyOutlined />}
            onClick={handleCopyField}
          />
        </div>
      </Col>
    </Row>
  );
};

//* list of single field
const SingleFieldList = ({ fields, items, fullKey, remove, productId }) => {
  if (!fields?.length) return null;

  const { type: typeOfItem, enums: enumsOfItem, format: formatOfItem } = items;

  return (
    <GroupElementWrapper
      style={{
        display: fields.length === 0 ? 'none' : null,
        marginRight: 40,
        marginBottom: 12,
        padding: '12px 12px 8px',
        width: '96%',
      }}
    >
      {fields?.map((fieldItem, idx) => {
        const errorKey = `${fullKey}[${fieldItem?.name}]`;

        return (
          <SingleFieldItemWrapper
            key={fieldItem.key}
            errorKey={errorKey}
            productId={productId}
          >
            <SingleFieldItem
              fieldItem={fieldItem}
              typeOfItem={typeOfItem}
              enumsOfItem={enumsOfItem}
              fullKey={fullKey}
              remove={remove}
              productId={productId}
              errorKey={errorKey}
              format={formatOfItem}
            />
          </SingleFieldItemWrapper>
        );
      })}
    </GroupElementWrapper>
  );
};

const SingleFieldItem = ({
  fieldItem,
  typeOfItem,
  enumsOfItem,
  fullKey,
  remove,
  productId,
  errorKey,
  format,
}) => {
  return (
    <Row>
      <Col style={{ flex: 1, overflow: 'hidden', marginTop: '4px 0' }}>
        <WalmartFields
          {...fieldItem}
          wrapperCol={{ span: 24 }}
          fieldListItemProps={fieldItem}
          type={typeOfItem}
          enums={enumsOfItem}
          fullKey={fullKey}
          showServerError={false}
          errorKey={errorKey}
          productId={productId}
          name={fieldItem?.name}
          format={format}
        />
      </Col>
      <Col>
        <RemoveListItemButton remove={remove} field={fieldItem} />
      </Col>
    </Row>
  );
};

const SingleFieldItemWrapper = (props) => {
  const { children, productId, errorKey } = props;

  const formItemServerError = useGetWalmartFormItemError({
    productId,
    errorKey: errorKey,
  });

  return (
    <Row>
      <Col style={{ flex: 1, overflow: 'hidden' }}>{children}</Col>
      {formItemServerError && (
        <Col style={{ paddingTop: 2, marginLeft: 4 }}>
          <ServerErrorIndicator serverError={formItemServerError} />
        </Col>
      )}
    </Row>
  );
};

export const RemoveListItemButton = ({ remove, field }) => {
  return (
    <Button
      type='danger'
      onClick={() => {
        remove(field.name);
      }}
      icon={<CloseCircleOutlined />}
      style={{
        borderColor: '#ff4d4f',
        backgroundColor: '#ff4d4f',
        borderRadius: 4,
      }}
    />
  );
};

const AddListItemButton = ({
  label,
  add,
  fullKey,
  listType = ['singleFieldList', 'multiFieldList'][0],
  isRequiredProperty,
  description,
  isRivirMappingField,
  rivirMappingField,
}) => {
  const {
    advancedModule: { foundFieldFullPath: advancedFoundFieldFullPath },
  } = useProductDetail();

  const formItemConfig = useMemo(() => {
    switch (listType) {
      case 'singleFieldList':
        return {
          style: { marginBottom: 4 },
          labelCol: { flex: '300px' },
        };
      case 'multipleFieldList':
        return {
          style: { paddingTop: 10, marginBottom: 4 },
          labelCol: { flex: '300px' },
        };
      default:
        return {};
    }
  }, [listType]);

  const fieldFullPath = 'walmartProductInformation.' + fullKey;

  return (
    <Col xs={24}>
      <Form.Item
        label={
          <>
            <Tooltip title={label}>
              <Text
                ellipsis
                className={classnames({
                  'product-detail-view__label-form-item': true,
                  'post-product-form-list__form-item--required':
                    isRequiredProperty,
                  [`product-detail-view__label-form-item--${fieldFullPath}`]: true,
                  'product-detail-view__label-form-item--found-field':
                    fieldFullPath?.toLowerCase() ===
                    advancedFoundFieldFullPath?.toLowerCase(),
                })}
                strong
              >
                {label}
              </Text>
            </Tooltip>
            {isRivirMappingField && (
              <Tooltip
                title={
                  rivirMappingField
                    ? `Auto populate from RIVIR: ${rivirMappingField}`
                    : ''
                }
                color='#238dd9'
              >
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
              </Tooltip>
            )}
            {description && (
              <Tooltip title={description} color='#238dd9'>
                <QuestionCircleOutlined className='product-detail-view__hint-button' />
              </Tooltip>
            )}
          </>
        }
        {...formItemConfig}
      >
        <Button
          type='default'
          onClick={() => add()}
          icon={<PlusOutlined />}
          style={{ borderRadius: 4 }}
        >
          Add value
        </Button>
      </Form.Item>
    </Col>
  );
};

export { PostFieldList };
